import { type PropsWithChildren, type ReactNode, useEffect, useState } from "react";
import { router, usePage } from "@inertiajs/react";
import { Avatar, Box, Button, Dialog, DropdownMenu, Flex, Heading, Text } from "@radix-ui/themes";
import ReactMarkdown from "react-markdown";
import { TimeTravelBanner } from "@/components/time-travel-banner";
import type { AuthUser, SharedProps } from "@/lib/types";
import type { ModuleKey } from "@/lib/grid-contract";

const SCREEN_OPTIONS: { id: ModuleKey; label: string }[] = [
  { id: "my_projects", label: "My Projects" },
  { id: "weekly_planner", label: "Weekly Portfolio Planner" },
  { id: "effort_projections", label: "Effort Projections" },
  { id: "payment_planner", label: "Payment Planner" },
  { id: "cash_flow", label: "Cash Flow" },
];

const SETTINGS_OPTIONS = [
  { label: "Users", path: "/admin/users" },
  { label: "Projects", path: "/projects" },
  { label: "Integrations", path: "/settings/integrations" },
  { label: "Project Groups", path: "/settings/project_groups" },
  { label: "API Tokens", path: "/settings/api_keys" },
];

const DESKTOP_NAV_MEDIA_QUERY = "(min-width: 1024px)";

function findActiveSetting(path: string, options: readonly { label: string; path: string }[]) {
  return options.find(
    (setting) => path === setting.path || path.startsWith(`${setting.path}/`),
  );
}

type AppLayoutProps = PropsWithChildren<{
  user: AuthUser | null;
  activeModule: ModuleKey;
  onModuleChange: (value: ModuleKey) => void;
  pageTitle?: string;
  contextControls?: ReactNode;
  pageActions?: ReactNode;
}>;

function AboutChangelogBody({ markdown }: { markdown: string }) {
  const trimmed = markdown.trim();
  if (!trimmed) {
    return <Text size="2">Changelog is not available yet.</Text>;
  }
  return (
    <Box className="about-changelog">
      <ReactMarkdown>{trimmed}</ReactMarkdown>
    </Box>
  );
}

function isDesktopNavViewport() {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return true;
  return window.matchMedia(DESKTOP_NAV_MEDIA_QUERY).matches;
}

export function AppLayout({
  children,
  user,
  activeModule,
  onModuleChange,
  contextControls,
  pageActions,
}: AppLayoutProps) {
  const [aboutOpen, setAboutOpen] = useState(false);
  const [desktopNavVisible, setDesktopNavVisible] = useState(isDesktopNavViewport);
  const page = usePage<SharedProps>();
  const appInfo = page.props.app_info;

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const mediaQueryList = window.matchMedia(DESKTOP_NAV_MEDIA_QUERY);
    const updateMatch = (event: MediaQueryListEvent) => setDesktopNavVisible(event.matches);

    setDesktopNavVisible(mediaQueryList.matches);
    mediaQueryList.addEventListener("change", updateMatch);

    return () => {
      mediaQueryList.removeEventListener("change", updateMatch);
    };
  }, []);

  function navigateTo(path: string) {
    router.visit(path);
  }

  function handleModuleChange(module: ModuleKey) {
    onModuleChange(module);

    const currentPath = page.url?.split("?")[0] || "/";
    if (currentPath !== "/") {
      router.visit(`/?module=${encodeURIComponent(module)}`);
    }
  }

  function handleLogout() {
    router.delete("/users/sign_out", {
      onFinish: () => {
        window.location.assign("/users/sign_in");
      },
    });
  }

  const currentPath = page.url?.split("?")[0] || "/";
  const settingsNavOptions = [
    ...SETTINGS_OPTIONS,
    ...(page.props.development_features?.show_mock_integration
      ? [{ label: "Mock integrations (dev)", path: "/admin/mock_integration" }]
      : []),
    ...(page.props.development_features?.show_time_travel
      ? [{ label: "Time travel (dev)", path: "/admin/time_travel" }]
      : []),
  ];
  const activeModuleLabel = SCREEN_OPTIONS.find((option) => option.id === activeModule)?.label;
  const activeSetting = findActiveSetting(currentPath, settingsNavOptions);
  const currentNavLabel = activeSetting
    ? `Settings - ${activeSetting.label}`
    : activeModuleLabel || "Menu";

  return (
    <Box className="min-h-screen bg-muted/30">
      <Box className="sticky top-0 z-40 border-b bg-background">
        <Box px="4">
          <Flex
            data-testid="app-header-row-1"
            className="items-center justify-between gap-3"
            py="3"
          >
            <Flex align="center" gap="3" className="min-w-0">
              <Heading size="4" className="text-xl md:text-2xl">
                PBrain
              </Heading>
              {desktopNavVisible ? (
                <Box data-testid="desktop-primary-nav-wrap">
                  <nav
                    aria-label="Primary navigation"
                    data-testid="desktop-primary-nav"
                    className="flex items-center gap-1.5 xl:gap-2"
                  >
                    {SCREEN_OPTIONS.map((option) => {
                      const active = !activeSetting && option.id === activeModule;
                      return (
                        <Button
                          key={option.id}
                          size="1"
                          variant={active ? "solid" : "soft"}
                          onClick={() => handleModuleChange(option.id)}
                          aria-current={active ? "page" : undefined}
                        >
                          {option.label}
                        </Button>
                      );
                    })}
                    <DropdownMenu.Root>
                      <DropdownMenu.Trigger>
                        <Button
                          variant={activeSetting ? "solid" : "outline"}
                          size="1"
                          aria-current={activeSetting ? "page" : undefined}
                        >
                          Settings
                        </Button>
                      </DropdownMenu.Trigger>
                      <DropdownMenu.Content align="start">
                        {settingsNavOptions.map((setting) => {
                          const active = activeSetting?.path === setting.path;
                          return (
                            <DropdownMenu.Item
                              key={setting.path}
                              onSelect={() => navigateTo(setting.path)}
                            >
                              {active ? `${setting.label} (Current)` : setting.label}
                            </DropdownMenu.Item>
                          );
                        })}
                      </DropdownMenu.Content>
                    </DropdownMenu.Root>
                  </nav>
                </Box>
              ) : (
                <Box data-testid="mobile-primary-nav-wrap">
                  <DropdownMenu.Root>
                    <DropdownMenu.Trigger>
                      <Button
                        className="max-w-[13rem] truncate"
                        size="2"
                        variant="outline"
                        aria-label="Main navigation menu"
                      >
                        {currentNavLabel}
                      </Button>
                    </DropdownMenu.Trigger>
                    <DropdownMenu.Content align="start">
                      <DropdownMenu.Label>Navigate</DropdownMenu.Label>
                      <DropdownMenu.Separator />
                      {SCREEN_OPTIONS.map((option) => {
                        const active = !activeSetting && option.id === activeModule;
                        return (
                          <DropdownMenu.Item
                            key={option.id}
                            onSelect={() => handleModuleChange(option.id)}
                          >
                            {active ? `${option.label} (Current)` : option.label}
                          </DropdownMenu.Item>
                        );
                      })}
                      <DropdownMenu.Separator />
                      <DropdownMenu.Label>Settings</DropdownMenu.Label>
                      <DropdownMenu.Separator />
                      {settingsNavOptions.map((setting) => {
                        const active = activeSetting?.path === setting.path;
                        return (
                          <DropdownMenu.Item
                            key={setting.path}
                            onSelect={() => navigateTo(setting.path)}
                          >
                            {active ? `${setting.label} (Current)` : setting.label}
                          </DropdownMenu.Item>
                        );
                      })}
                    </DropdownMenu.Content>
                  </DropdownMenu.Root>
                </Box>
              )}
            </Flex>

            <Flex align="center" gap="2" className="shrink-0">
              <DropdownMenu.Root>
                <DropdownMenu.Trigger>
                  <Button variant="ghost" size="1" aria-label="User menu">
                    <Avatar
                      fallback={user?.email?.slice(0, 2).toUpperCase() || "NA"}
                      radius="full"
                    />
                  </Button>
                </DropdownMenu.Trigger>
                <DropdownMenu.Content align="end">
                  <DropdownMenu.Label>
                    <Text size="1">{user?.email || "User"}</Text>
                  </DropdownMenu.Label>
                  <DropdownMenu.Separator />
                  <DropdownMenu.Item onSelect={() => setAboutOpen(true)}>About</DropdownMenu.Item>
                  <DropdownMenu.Item onSelect={handleLogout}>Log Out</DropdownMenu.Item>
                </DropdownMenu.Content>
              </DropdownMenu.Root>
            </Flex>
          </Flex>

          {desktopNavVisible && activeSetting ? (
            <Flex
              data-testid="settings-submenu"
              className="items-center gap-1.5 border-t py-2"
              wrap="wrap"
            >
              {settingsNavOptions.map((setting) => {
                const settingIsActive = setting.path === activeSetting.path;
                return (
                  <Button
                    key={setting.path}
                    size="1"
                    variant={settingIsActive ? "solid" : "soft"}
                    aria-current={settingIsActive ? "page" : undefined}
                    onClick={() => navigateTo(setting.path)}
                  >
                    {setting.label}
                  </Button>
                );
              })}
            </Flex>
          ) : null}

          <Flex data-testid="app-header-row-2" className="flex-col items-stretch gap-2" py="2">
            <Flex align="center" gap="2" wrap="wrap" className="w-full">
              {contextControls}
            </Flex>
            <Flex align="center" gap="2" wrap="wrap" className="w-full justify-start">
              {pageActions}
            </Flex>
          </Flex>
        </Box>
      </Box>
      <TimeTravelBanner />

      <Box data-testid="app-content" px="4" pt="2" pb="4">
        {children}
      </Box>

      <Dialog.Root open={aboutOpen} onOpenChange={setAboutOpen}>
        <Dialog.Content maxWidth="760px">
          <Dialog.Title>About PBrain</Dialog.Title>
          <Dialog.Description size="2">Version {appInfo?.version || "unknown"}</Dialog.Description>
          <Box
            mt="3"
            p="3"
            style={{
              maxHeight: "360px",
              overflowY: "auto",
              border: "1px solid var(--gray-6)",
              borderRadius: "var(--radius-3)",
              backgroundColor: "var(--gray-2)",
            }}
          >
            <AboutChangelogBody
              markdown={appInfo?.changelog || "Changelog is not available yet."}
            />
          </Box>
          <Flex justify="end" mt="4">
            <Dialog.Close>
              <Button variant="soft">Close</Button>
            </Dialog.Close>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>
    </Box>
  );
}
